import { keyBy } from 'lodash/fp';
import { useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { useDataContextSchema } from 'dashboard/lib/hooks/useDataContextSchema';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { getPriorityWorkflowVersion } from 'dashboard/lib/workflows/util';
import { Configuration } from 'dashboard/models/grid-layout';
import { TransactionType, TransactionTypeAttributes } from 'dashboard/models/transaction-type';
import {
  TransactionModule,
  TransactionTypeGridLayout,
} from 'dashboard/models/transaction-type-grid-layout';
import { Trigger, TriggerComputedField } from 'dashboard/models/trigger';
import { Workflow, WorkflowStatus } from 'dashboard/models/workflow';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import { clearTransactionType } from 'dashboard/redux/actions/transaction-type';
import {
  useActivateTransactionTypeMutation,
  useDeactivateTransactionTypeMutation,
} from 'dashboard/redux/api/transaction-type';
import { getAllEnabledTemplates } from 'dashboard/redux/selectors/organization';
import { getPublishedVerificationTemplates } from 'dashboard/redux/selectors/verification-templates';
import {
  addTransactionTypeGridLayout,
  setTransactionTypeGridLayout,
} from 'dashboard/redux/slices/transaction-type';
import { RootState } from 'dashboard/redux/store';
import {
  activateTransactionTypeField,
  deactivateTransactionTypeField,
  loadTransactionType,
  updateTransactionType,
} from 'dashboard/redux/thunk-actions/transaction-type';
import { loadWorkflows } from 'dashboard/redux/thunk-actions/workflows';
import { useObjectSchemas } from 'dashboard/routes/DashboardTransactionTypesShowRoute/useObjectSchemas';
import DashboardTransactionTypesShowView from 'dashboard/views/DashboardTransactionTypesShowView';
import { isComputedField } from 'lib/fields';
import { fieldSchemaErrorMessage, isFieldSchemaError } from 'lib/fields-errors';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import useRedirectOnEnvironmentChange from 'lib/hooks/useRedirectOnEnvironmentChange';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import { ApiModel } from 'lib/interfaces/api';
import {
  conflictErrorString,
  isConflictError,
  isPermissionsError,
  permissionsErrorMessage,
} from 'lib/permissions-errors';

type Params = { transactionTypeId: string };

const DashboardTransactionTypesShowRoute = (props: PropsFromRedux) => {
  useOnMountAndUnmount(props.clearTransactionType);

  useRedirectOnEnvironmentChange('/dashboard/transaction-types');

  const params = useParams<Params>() as Params;

  const { callback: handleLoadTransactionType } = useApiLoader({
    loader: () => props.loadTransactionType(params.transactionTypeId),
    deps: [props.loadTransactionType, params.transactionTypeId],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load transaction type.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const layoutVariables = useMemo(
    () => props.transactionVariableDefinitions.map((v) => v.attributes['variable-definition']),
    [props.transactionVariableDefinitions],
  );

  const { getObjectSchema } = useObjectSchemas();
  const { dataSchema } = useDataContextSchema(layoutVariables, getObjectSchema);

  const isComputedFieldsEnabled = useFeatureFlag(
    'dashboard:enable-workflows-computed-field-triggers',
  );
  const hasComputedFields = props.transactionType?.attributes?.['field-schemas']
    .filter((type) => type.config['deactivated-at'] == null)
    .some((fieldSchema) => isComputedField(fieldSchema));

  const [deactivateTransactionType, { error: deactivateTransactionTypeError }] =
    useDeactivateTransactionTypeMutation();
  const handleDeactivateTransactionType = async (transactionTypeId: string) => {
    await deactivateTransactionType(transactionTypeId);
    await props.loadTransactionType(params.transactionTypeId);
  };

  useRTKErrorHandler({
    error: deactivateTransactionTypeError,
    onError: () => ({
      message: 'Failed to deactivate transaction type. Please try again later.',
      level: MessageType.Failure,
    }),
  });

  const [activateTransactionType, { error: activateTransactionTypeError }] =
    useActivateTransactionTypeMutation();

  const handleActivateTransactionType = async (transactionTypeId: string) => {
    await activateTransactionType(transactionTypeId);
    await props.loadTransactionType(params.transactionTypeId);
  };

  useRTKErrorHandler({
    error: activateTransactionTypeError,
    onError: () => ({
      message: 'Failed to activate transaction type. Please try again later.',
      level: MessageType.Failure,
    }),
  });

  useApiLoader({
    loader: async () => {
      await props.loadWorkflows({
        triggerTypes: [ApiModel.TriggerComputedField],
      });
    },
    deps: [props.loadWorkflows, props.transactionType],
    skip: !isComputedFieldsEnabled || !hasComputedFields,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load workflows for computed fields.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const fieldKeyToWorkflowWithRelationships: Record<
    string,
    { workflow: Workflow; workflowVersion: WorkflowVersion; trigger: Trigger }
  > = useMemo(() => {
    const workflowDeploymentMap = keyBy('id', props.workflowDeployments);
    const filteredTriggers = props.triggers.filter(
      (trigger): trigger is TriggerComputedField =>
        trigger.type === ApiModel.TriggerComputedField &&
        trigger.attributes['field-schema-object-token'] === props.transactionType?.id,
    );
    const triggersMap = keyBy('id', filteredTriggers);

    const filteredWorkflows = props.workflows
      .filter((workflow) => workflow.attributes.status !== WorkflowStatus.Archived)
      // Retrieve related workflow version ID
      .map((workflow): [Workflow, string] => [
        workflow,
        getPriorityWorkflowVersion(workflow, workflowDeploymentMap),
      ])
      // Retrieve workflow version
      .map(([workflow, versionId]): [Workflow, WorkflowVersion] => [
        workflow,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        props.workflowVersions.find((wfv) => wfv.id === versionId),
      ])
      // Filter out nil workflow versions
      .filter(([_workflow, workflowVersion]) => workflowVersion != null);

    return filteredWorkflows.reduce(
      (
        result: Record<
          string,
          { workflow: Workflow; workflowVersion: WorkflowVersion; trigger: Trigger }
        >,
        [workflow, workflowVersion],
      ) => {
        const trigger = triggersMap[workflowVersion.relationships.trigger.data.id];
        if (trigger) {
          result[trigger.attributes['field-key']] = {
            workflow,
            workflowVersion,
            trigger,
          };
        }

        return result;
      },
      {},
    );
  }, [
    props.workflows,
    props.workflowVersions,
    props.workflowDeployments,
    props.triggers,
    props.transactionType,
  ]);

  const {
    callback: updateTransactionType,
    loading: isUpdatingTransactionType,
    error: updateTransactionTypeError,
  } = useApiCallback(
    async (attributes: TransactionTypeAttributes, deactivateFieldSchemaKeys: string[]) => {
      const id = params.transactionTypeId;

      await SessionClientDashboard.updateResource<TransactionType>(
        {
          id: id,
          attributes: {
            'field-schemas': attributes['field-schemas'],
            'relation-schemas': attributes['relation-schemas'],
            identifiers: attributes.identifiers,
            name: attributes.name,
          },
          type: ApiModel.TransactionType,
        },
        {
          meta: {
            'deactivate-field-schema-keys': deactivateFieldSchemaKeys,
          },
        },
      );
      await handleLoadTransactionType();
      props.showMessage('Successfully saved transaction type.', MessageType.Success, true);
    },

    [props.currentEnvironment, props.showMessage],
  );

  const { callback: updateTransactionTypeGridLayout, error: updateTransactionTypeGridLayoutError } =
    useApiCallback(
      async (gridLayout: TransactionTypeGridLayout) => {
        const response =
          await SessionClientDashboard.updateResource<TransactionTypeGridLayout>(gridLayout);

        props.setTransactionTypeGridLayout(response.data);
        props.showMessage('Successfully updated layout.', MessageType.Success);
      },
      [props.showMessage],
    );

  const { callback: createTransactionTypeGridLayout, error: createTransactionTypeGridLayoutError } =
    useApiCallback(
      async (configuration: Configuration<TransactionModule>) => {
        const response = await SessionClientDashboard.postResourceCreate<
          TransactionTypeGridLayout,
          {
            type: ApiModel.TransactionTypeGridLayout;
            attributes: {
              'txn-type-id': string;
              name: string;
              configuration: Configuration<TransactionModule>;
            };
          }
        >({
          type: ApiModel.TransactionTypeGridLayout,
          attributes: {
            'txn-type-id': params.transactionTypeId,
            name: 'Default',
            configuration,
          },
        });

        props.addTransactionTypeGridLayout(response.data);
        // as far as users are concerned, they've just updated the default layout
        props.showMessage('Successfully updated layout.', MessageType.Success);
      },
      [props.showMessage],
    );
  useApiErrorHandler({
    error: createTransactionTypeGridLayoutError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to save layout.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiErrorHandler({
    error: updateTransactionTypeGridLayoutError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to save layout.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiErrorHandler({
    error: updateTransactionTypeError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (isConflictError(error)) {
        props.showMessage(conflictErrorString(error), MessageType.Failure);
      } else if (isFieldSchemaError(error)) {
        props.showMessage(fieldSchemaErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to save transaction type.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  return (
    <>
      {props.transactionType?.attributes && (
        <DashboardTransactionTypesShowView
          activateTransactionTypeField={props.activateTransactionTypeField}
          deactivateTransactionTypeField={props.deactivateTransactionTypeField}
          activateTransactionType={handleActivateTransactionType}
          deactivateTransactionType={handleDeactivateTransactionType}
          transactionType={props.transactionType}
          updateTransactionType={props.updateTransactionType}
          showMessage={props.showMessage}
          fieldKeyToWorkflowWithRelationships={fieldKeyToWorkflowWithRelationships}
          organization={props.organization}
          currentEnvironment={props.currentEnvironment}
          onUpdateTransactionType={updateTransactionType}
          isUpdatingTransactionType={isUpdatingTransactionType}
          dataSchema={dataSchema}
          enabledInquiryTemplates={props.enabledTemplates}
          publishedVerificationTemplates={props.publishedVerificationTemplates}
          gridLayouts={props.gridLayouts}
          onUpdateTransactionTypeGridLayout={updateTransactionTypeGridLayout}
          onCreateTransactionTypeGridLayout={createTransactionTypeGridLayout}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    transactionType: state.transactionType.transactionType,
    transactionVariableDefinitions: state.transactionType.transactionVariableDefinitions,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    organization: state.organization.organization,
    triggers: state.workflows.triggers,
    workflows: state.workflows.workflows,
    workflowVersions: state.workflows.workflowVersions,
    workflowDeployments: state.workflows.workflowDeployments,
    enabledTemplates: getAllEnabledTemplates(state),
    publishedVerificationTemplates: getPublishedVerificationTemplates(state),
    gridLayouts: state.transactionType.gridLayouts,
  };
};

const mapDispatchToProps = {
  activateTransactionTypeField,
  deactivateTransactionTypeField,
  clearTransactionType,
  loadTransactionType,
  updateTransactionType,
  showMessage,
  loadWorkflows,
  addTransactionTypeGridLayout,
  setTransactionTypeGridLayout,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardTransactionTypesShowRoute);
